import { useReducer } from "react";
import type { Deck as DeckType, Slide, SlideStatus } from "@/common/decks";

// Define action types
type DeckAction =
  | { type: "UPDATE_DECK_TITLE"; payload: string }
  | { type: "UPDATE_SLIDE_TITLE"; payload: { slideId: string; title: string } }
  | {
      type: "UPDATE_SPEAKER_NOTES";
      payload: { slideId: string; content: string };
    }
  | { type: "MOVE_SLIDE_UP"; payload: string }
  | { type: "MOVE_SLIDE_DOWN"; payload: string }
  | { type: "DELETE_SLIDE"; payload: string }
  | { type: "REORDER_SLIDES"; payload: { fromIndex: number; toIndex: number } }
  | { type: "ADD_SLIDE"; payload: Slide | Slide[] }
  | { type: "SET_DECK"; payload: DeckType }
  | { type: "RECONCILE_PENDING_STATUSES"; payload: DeckType };

const deckReducer = (
  state: DeckType | null,
  action: DeckAction,
): DeckType | null => {
  if (action.type === "SET_DECK") {
    // Preserve client-side terminal "error" status across server-driven updates.
    // Once a slide is marked as error locally (e.g. timed-out by RECONCILE_PENDING_STATUSES),
    // a subsequent save round-trip would otherwise revive the server's stale pending/processing
    // status and re-trigger the save loop.
    let payload = action.payload;
    if (state) {
      const errorById = new Map(
        state.slides
          .filter((s) => s.status === "error")
          .map((s) => [s.id, s] as const),
      );
      if (errorById.size > 0) {
        payload = {
          ...payload,
          slides: payload.slides.map((s) => {
            const localErr = errorById.get(s.id);
            if (!localErr) return s;
            return {
              ...s,
              status: "error" as SlideStatus,
              error_message: localErr.error_message,
            };
          }),
        };
      }
    }
    if (JSON.stringify(state) === JSON.stringify(payload)) {
      return state; // No change, return current state
    }
    return payload;
  }

  if (!state) {
    return null;
  }

  switch (action.type) {
    case "UPDATE_DECK_TITLE":
      return {
        ...state,
        name: action.payload,
        updated_at: new Date(),
      };

    case "UPDATE_SLIDE_TITLE":
      return {
        ...state,
        slides: state.slides.map((slide) =>
          slide.id === action.payload.slideId
            ? { ...slide, title: action.payload.title }
            : slide,
        ),
        updated_at: new Date(),
      };

    case "UPDATE_SPEAKER_NOTES":
      return {
        ...state,
        slides: state.slides.map((slide) =>
          slide.id === action.payload.slideId
            ? { ...slide, speaker_notes: action.payload.content }
            : slide,
        ),
        updated_at: new Date(),
      };

    case "MOVE_SLIDE_UP": {
      const slides = [...state.slides];
      const index = slides.findIndex((slide) => slide.id === action.payload);
      if (index > 0) {
        [slides[index], slides[index - 1]] = [slides[index - 1], slides[index]];
      }
      return {
        ...state,
        slides,
        updated_at: new Date(),
      };
    }

    case "MOVE_SLIDE_DOWN": {
      const slides = [...state.slides];
      const index = slides.findIndex((slide) => slide.id === action.payload);
      if (index < slides.length - 1) {
        [slides[index], slides[index + 1]] = [slides[index + 1], slides[index]];
      }
      return {
        ...state,
        slides,
        updated_at: new Date(),
      };
    }

    case "DELETE_SLIDE":
      return {
        ...state,
        slides: state.slides.filter((slide) => slide.id !== action.payload),
        updated_at: new Date(),
      };

    case "REORDER_SLIDES": {
      const slides = [...state.slides];
      const [movedSlide] = slides.splice(action.payload.fromIndex, 1);
      slides.splice(action.payload.toIndex, 0, movedSlide);
      return {
        ...state,
        slides,
        updated_at: new Date(),
      };
    }

    case "ADD_SLIDE": {
      const slidesToAdd = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];
      return {
        ...state,
        slides: [...slidesToAdd, ...state.slides],
        updated_at: new Date(),
      };
    }

    case "RECONCILE_PENDING_STATUSES": {
      // user may be reordering slides or deleteing them while they are pending,
      // so we need to reconcile the pending statuses with the new deck state from the server
      // we search for matching slide IDs in the state, and if the status has changed from pending/processing to something else, we update the state
      // otherwise we do nothing to avoid triggering rerender or overwriting user changes
      const newSlides = state.slides.map((slide) => {
        const updatedSlide = action.payload.slides.find(
          (s) => s.id === slide.id,
        );
        if (!updatedSlide) {
          // If the slide is no longer present in the updated deck, we can assume it was deleted, so we mark it as error to indicate it won't be generated
          if (["pending", "processing"].includes(slide.status)) {
            return {
              ...slide,
              status: "error" as SlideStatus,
              error_message: "Slide was deleted",
            };
          }
          return slide;
        }

        if (new Date().getTime() - state.created_at.getTime() > 1000 * 60 * 5) {
          // If the deck is older than 5 minutes, we can mark any pending or processing slides as error, since they likely got stuck
          // TODO this won't be necessary once slide generation is moved to portal.

          if (["pending", "processing"].includes(slide.status)) {
            console.log(`marked slide ${slide.id} as error due to timeout`);
            return {
              ...slide,
              status: "error" as SlideStatus,
              error_message: "Slide generation timed out",
            };
          }
          return slide;
        }
        if (
          (slide.status === "pending" || slide.status === "processing") &&
          updatedSlide.status !== slide.status
        ) {
          return {
            ...slide,
            status: updatedSlide.status as SlideStatus,
            speaker_notes: updatedSlide.speaker_notes, // update speaker notes in case they were generated while the slide was pending
          };
        }
        return slide;
      });
      const newState = { ...state, slides: newSlides };
      if (JSON.stringify(state) === JSON.stringify(newState)) {
        return state; // No change, return current state
      }
      return newState;
    }

    default:
      return state;
  }
};

// Custom hook to use the deck reducer
export const useDeckReducer = (initialDeck: DeckType | null) => {
  return useReducer(deckReducer, initialDeck);
};
